// Compact stand-in for a native <select multiple> on phones.
//
// On a phone the mobile-dropdown bottom sheet — not the inline list — is how a
// multi-select is actually operated, so rendering every option inline is pure
// cost: the five coupon Product Filters alone measured ~1000px of list boxes,
// making that one card 1.6x the viewport. This collapses the native control and
// puts a single-line summary trigger in its place, matching the other fields on
// the form.
//
// The <select> stays in the DOM and remains the source of truth — it is what the
// sheet reads and writes, and what the form submits. The trigger is only a
// proxy: tapping it re-dispatches `mousedown` on the select, which is what the
// globally-delegated mobile-dropdown handler listens for.

// Presentation marker: the native control is collapsed out of the layout.
const NATIVE_HIDDEN_CLASS = "gl-mobile-select-native"

// Routing marker: mobile_dropdown_controller's delegated handler matches on a
// fixed list of classes, and callers do not all use gl-select — the
// licensed-states partial renders `class: "select2"`, the import wizard
// `gl-wizard-pref-control`. Keying either the hide CSS or the sheet handler off
// the element's own classes would leave those fields with a full inline list and
// a dead trigger. Stamp our own class instead and have both systems match it.
const SHEET_SELECT_CLASS = "gl-mobile-sheet-select"

function labelTextFor(select) {
  if (!select.id) return null
  return document.querySelector(`label[for="${select.id}"]`)?.textContent.trim() || null
}

// A blank option is a placeholder, not a real pick — the coupon filters submit
// an empty value alongside the real ones to allow clearing.
function chosenOptions(select) {
  return Array.from(select.selectedOptions).filter((option) => option.value !== "")
}

export function attachMobileSelectSummary(select, { placeholder = "Select options" } = {}) {
  if (!select) return () => {}

  select.classList.add(NATIVE_HIDDEN_CLASS, SHEET_SELECT_CLASS)
  // Keep it out of the tab order: the trigger is the operable control, and a
  // focused <select> is exactly what makes iOS try to open its own picker.
  select.setAttribute("tabindex", "-1")
  // ...and out of the accessibility tree, so a rotor user does not meet two
  // entries for one field. The trigger carries the accessible name.
  select.setAttribute("aria-hidden", "true")

  const trigger = document.createElement("button")
  trigger.type = "button"
  trigger.className = "gl-mobile-select-summary"
  // "dialog", not "listbox": mobile_dropdown_controller#buildOverlay renders
  // role="dialog" aria-modal="true", so announcing a listbox would describe the
  // wrong popup type. (No aria-expanded — the sheet has no hook back into this
  // trigger to signal open/close, and a stale value is worse than none.)
  trigger.setAttribute("aria-haspopup", "dialog")

  const summary = document.createElement("span")
  summary.className = "gl-mobile-select-summary-label"
  trigger.appendChild(summary)

  function render() {
    const chosen = chosenOptions(select)

    summary.textContent = chosen.length ? chosen.map((option) => option.text).join(", ") : placeholder
    trigger.classList.toggle("gl-mobile-select-summary--placeholder", chosen.length === 0)
    // Mirrors a lock applied by the owning form (e.g. the coupon form pinning
    // Bucket to "fresh"), so the trigger can't reopen what the form closed.
    trigger.disabled = select.disabled

    const existingCount = trigger.querySelector(".gl-mobile-select-summary-count")
    if (chosen.length > 1) {
      const count = existingCount || document.createElement("span")
      count.className = "gl-mobile-select-summary-count"
      count.textContent = String(chosen.length)
      if (!existingCount) trigger.appendChild(count)
    } else if (existingCount) {
      existingCount.remove()
    }

    const fieldLabel = labelTextFor(select)
    trigger.setAttribute("aria-label", fieldLabel ? `${fieldLabel}: ${summary.textContent}` : summary.textContent)
  }

  function onChange() {
    render()
  }

  function onTriggerClick() {
    if (select.disabled) return
    // Hand the tap back to the select; the delegated mobile-dropdown handler on
    // <body> matches on the select and opens the sheet.
    select.dispatchEvent(new Event("mousedown", { bubbles: true, cancelable: true }))
  }

  select.addEventListener("change", onChange)
  trigger.addEventListener("click", onTriggerClick)

  select.insertAdjacentElement("afterend", trigger)
  render()

  return () => {
    select.removeEventListener("change", onChange)
    trigger.removeEventListener("click", onTriggerClick)
    trigger.remove()
    select.classList.remove(NATIVE_HIDDEN_CLASS, SHEET_SELECT_CLASS)
    select.removeAttribute("tabindex")
    select.removeAttribute("aria-hidden")
  }
}
